// Helper function to wait for a specified number of seconds
const wait = (seconds: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, seconds * 1000));
};

/**
 * Fetches a URL with automatic retry logic for 429 (Too Many Requests) responses.
 * Respects the Retry-After header when present, falls back to exponential backoff otherwise.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @param maxRetries - Maximum number of retries (default: 3)
 * @returns Promise that resolves to the fetch Response
 */
export const fetchWithRetry = async (
  url: string,
  options: RequestInit,
  maxRetries: number = 3
): Promise<Response> => {
  let attempt = 0;

  while (attempt <= maxRetries) {
    const response = await fetch(url, options);

    if (response.status === 429) {
      if (attempt === maxRetries) {
        // Max retries reached, return the 429 response
        return response;
      }

      // Get the Retry-After header
      const retryAfter = response.headers.get('Retry-After');
      const waitTime = retryAfter ? parseInt(retryAfter, 10) : Math.pow(2, attempt); // Fallback to exponential backoff

      console.warn(
        `Received 429 Too Many Requests. Retrying after ${waitTime} seconds (attempt ${attempt + 1}/${maxRetries + 1})`
      );

      await wait(waitTime);
      attempt++;
      continue;
    }

    // Return response for any other status code (success or other errors)
    return response;
  }

  // This shouldn't be reached, but TypeScript needs it
  throw new Error('Unexpected retry logic error');
};
